import { useCallback, FC, useMemo, useRef } from "react";
import { MdsItemListRowDropdown } from "@/design-system/components/item-list/MdsItemListRowDropdown";
import { MdsItemKind, MdsItemListItemRowData } from "@/design-system/components/item-list/types";
import { ReactMouseEvent } from "@/domains/react/types";
import { MdsSpacer } from "@/design-system/components/spacer";
import { observer } from "mobx-react-lite";
import { MdsItemListItemSharedByBox } from "@/design-system/components/item-list/rows/MdsItemListItemSharedByBox";
import { Icon } from "@/design-system/components/item-list/rows/icon/icon";
import styled from "@emotion/styled";
import { TitleIconComponent } from "@/design-system/components/item-list/rows/title-icon/title-icon";
import { MDSRowItem, MDSRowItemSize, MDSRowItemSlotName, MDSTextColor, MDSTextWeight } from "@/mds";
import { MDSText, MDSTextVariant } from "@/mds";
import { mdsPanelWidthsValue } from "@/design-system/foundations/breakpoints";

export interface MdsItemListItemRowProps {
  data: MdsItemListItemRowData;
  hasSuggestion?: boolean;
  noSeparator?: boolean;
}

/**
 * @deprecated - Use the newer MDS components instead.
 *
 * - If something doesn't exist yet, please create a new MDS component and
 *   have it reviewed by the team!
 *
 * - If something needs to hook into our `stores`, add it to the /components directory.
 *
 * - If something is a one-off; please colocate it with the component/page that
 *   needs it.
 *
 * - If you have questions, please ask in the #hub-eng-web-team Slack channel.
 */
export const MdsItemListItemRow: FC<MdsItemListItemRowProps> = observer(
  ({
    data: { payload: item, size = MDSRowItemSize.Medium, mustClickOnRowTitle = false },
    hasSuggestion,
    noSeparator,
  }) => {
    const itemPreviewState = useMemo(() => item.createPreviewState?.(), [item]);

    const extraRowContent = useMemo(
      () => item.extraRow && { id: item.extraRow.id, content: item.extraRow.content?.() },
      [item]
    );

    const handleMouseEnter = item.listState?.isHoverable
      ? () => item.listState?.highlightItem(item.id)
      : undefined;

    const isHighlighted = item.id === item.listState?.highlightedItemId;
    const isSelected = item.listState?.isSelected(item.id);

    const itemChips = item.chips?.() || null;
    const hasChips = !!itemChips;

    const itemRef = useRef(item);
    itemRef.current = item;

    const handleItemClick = useCallback((event: ReactMouseEvent, canClickItem = true) => {
      event.stopPropagation();
      const item = itemRef.current;
      if (item.listState?.canSelect && event.shiftKey) {
        item.listState.selectItemsFromLastSelectionUntil(item.id);
        return;
      }

      // if (item.listState?.isSelectModeActive || (isMac() ? event.metaKey : event.ctrlKey)) {
      //   item.listState?.toggleItemSelection(item.id);
      //   return;
      // }

      if (canClickItem) {
        item.onClick({ itemId: item.id, event });
      }
    }, []);

    const handleRowClick = (event: ReactMouseEvent) => handleItemClick(event, !mustClickOnRowTitle);

    const handleTitleClick = (event: ReactMouseEvent) => handleItemClick(event);

    const rightAlignedComponent = item.rightAlignedComponent?.(isHighlighted);
    const showActions =
      (!item.listState?.isSelectModeActive || !isSelected) &&
      (!!rightAlignedComponent || (isHighlighted && !!item.dropdown));
    const showChipsAndShare = !rightAlignedComponent || !showActions;

    const preview =
      itemPreviewState?.previewUi && itemPreviewState?.anchorPreviewAtTheEnd ? (
        <div slot={MDSRowItemSlotName.LastAddon}>{itemPreviewState.previewUi}</div>
      ) : (
        itemPreviewState?.previewUi
      );

    const canSelect = item.listState?.canSelect ?? false;

    const isSearchOrMessage = [MdsItemKind.Search, MdsItemKind.Message].includes(item.kind);

    return (
      <MDSRowItem
        $isSelected={isSelected}
        $isHighlighted={isHighlighted}
        $isSpecial={item.isSpecial}
        $size={size}
        $hasSuggestion={hasSuggestion}
        $mustClickOnRowTitle={mustClickOnRowTitle}
        noSeparator={noSeparator}
        className="mds-item-list-row" // Used in e2e tests
        onClick={handleRowClick}
        onMouseEnter={handleMouseEnter}
      >
        <Icon
          slot={MDSRowItemSlotName.LeftAddon}
          item={item}
          isSelected={isSelected}
          isHighlighted={canSelect && isHighlighted}
          itemRef={itemRef}
          handleItemClick={handleItemClick}
        />
        <ContentRow>
          <Content>
            <LabelContainer
              onClick={handleTitleClick}
              onMouseEnter={itemPreviewState?.handleMouseEnter}
              onMouseLeave={itemPreviewState?.handleMouseLeave}
            >
              {isSearchOrMessage && (
                <MDSText
                  color={MDSTextColor.Emphasis}
                  variant={MDSTextVariant.TextS}
                  weight={MDSTextWeight.Smedium}
                >
                  {item.label}
                </MDSText>
              )}
              {!isSearchOrMessage && (
                <MDSText variant={MDSTextVariant.TextM} weight={MDSTextWeight.Medium}>
                  {item.label}
                </MDSText>
              )}
              {showChipsAndShare && !!item.titleIcon && (
                <TitleIconComponent titleIcon={item.titleIcon} />
              )}
              {showChipsAndShare && (
                <ItemChipsContainerExpanded>{itemChips}</ItemChipsContainerExpanded>
              )}
            </LabelContainer>
          </Content>

          {!showActions && (
            <SharedByAndDateContainer
              $expandWidth={showChipsAndShare && !!item.sharedBy && !!item.dateLabel}
            >
              {showChipsAndShare && !!item.sharedBy && (
                <MdsItemListItemSharedByBox
                  sharedBy={item.sharedBy}
                  sharedByVariant={item.sharedByVariant}
                />
              )}
              {!!item.dateLabel && (
                <StyledText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
                  {item.dateLabel}
                </StyledText>
              )}
            </SharedByAndDateContainer>
          )}
        </ContentRow>

        {extraRowContent && (
          <BottomRow
            slot={MDSRowItemSlotName.BottomAddon}
            key={extraRowContent.id}
            onMouseEnter={itemPreviewState?.handleMouseEnter}
            onMouseLeave={itemPreviewState?.handleMouseLeave}
          >
            {!!hasChips && <ItemChipsContainerCollapsed>{itemChips}</ItemChipsContainerCollapsed>}
            <BottomRowContainer>{extraRowContent.content}</BottomRowContainer>
          </BottomRow>
        )}

        {preview}

        {showActions && (
          <RightSide
            $expandWidth={!!rightAlignedComponent && !!item.dropdown}
            slot={MDSRowItemSlotName.RightAddon}
          >
            {rightAlignedComponent}
            {item.dropdown && (
              <>
                <MdsSpacer />
                <MdsItemListRowDropdown itemId={item.id} dropdown={item.dropdown} />
              </>
            )}
          </RightSide>
        )}
      </MDSRowItem>
    );
  }
);

const ContentRow = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  flex-shrink: 1;
`;

const BottomRow = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.text.secondary};
  gap: ${({ theme }) => theme.spacing["2"]};
`;

const Content = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
`;

const LabelContainer = styled.div`
  overflow: hidden;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const RightSide = styled.div<{ $expandWidth?: boolean }>`
  align-items: flex-end;
  display: flex;
  margin-left: 10px;
  width: ${({ $expandWidth }) => ($expandWidth ? "auto" : "60px")};
  min-width: 60px;
`;

const StyledText = styled(MDSText)`
  justify-self: flex-end;
  text-align: right;
  line-height: 20px;
  flex-shrink: 1;
`;

const SharedByAndDateContainer = styled.div<{ $expandWidth?: boolean }>`
  display: flex;
  flex-direction: row;

  align-items: flex-end;
  gap: 16px;

  min-width: 0;
  margin-left: 10px;

  justify-content: ${({ $expandWidth }) => ($expandWidth ? "space-between" : "flex-end")};
  flex-shrink: ${({ $expandWidth }) => ($expandWidth ? 1 : 0)};
  width: ${({ $expandWidth }) => ($expandWidth ? "200px" : "auto")};

  @container mds-row-item (max-width: ${mdsPanelWidthsValue.headerBreakpoint}px) {
    width: min-content;
  }
`;

const ItemChipsContainerExpanded = styled.div`
  @container mds-row-item (max-width: ${mdsPanelWidthsValue.narrow}px) {
    display: none;
  }
`;

const BottomRowContainer = styled.div``;

const ItemChipsContainerCollapsed = styled.div`
  @container mds-row-item (min-width: ${mdsPanelWidthsValue.narrow}px) {
    display: none;
  }
`;
